import type { GameState } from "@bd/sim";

/** Local saves in IndexedDB (a late-game state is several MB, too big for localStorage). */
const DB = "box-and-dice";
const STORE = "saves";

function open(): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}

export interface SaveMeta {
  slot: string;
  company: string;
  week: number;
  savedAt: number;
}

export async function saveGame(slot: string, state: GameState): Promise<void> {
  const db = await open();
  await new Promise<void>((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    // solo saves: the player is the only company
    const meta: SaveMeta = { slot, company: state.companies[0]?.name ?? "?", week: state.week, savedAt: Date.now() };
    tx.objectStore(STORE).put({ meta, state: JSON.stringify(state) }, slot);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
  try {
    localStorage.setItem("bd:lastSlot", slot);
  } catch {
    /* private mode */
  }
}

export async function loadGame(slot: string): Promise<GameState | null> {
  const db = await open();
  return new Promise((res, rej) => {
    const req = db.transaction(STORE).objectStore(STORE).get(slot);
    req.onsuccess = () => res(req.result ? (JSON.parse(req.result.state) as GameState) : null);
    req.onerror = () => rej(req.error);
  });
}

export async function listSaves(): Promise<SaveMeta[]> {
  const db = await open();
  return new Promise((res, rej) => {
    const out: SaveMeta[] = [];
    const req = db.transaction(STORE).objectStore(STORE).openCursor();
    req.onsuccess = () => {
      const c = req.result;
      if (c) {
        out.push(c.value.meta);
        c.continue();
      } else res(out.sort((a, b) => b.savedAt - a.savedAt));
    };
    req.onerror = () => rej(req.error);
  });
}

export async function deleteSave(slot: string): Promise<void> {
  const db = await open();
  await new Promise<void>((res) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(slot);
    tx.oncomplete = () => res();
  });
}

export function lastSlot(): string | null {
  try {
    return localStorage.getItem("bd:lastSlot");
  } catch {
    return null;
  }
}
